/**
 * Utility to clean and format property addresses.
 * - Removes ", USA" / ", United States"
 * - Strips ZIP+4 extensions (e.g. 95070-3335 → 95070)
 * - Title-cases ALL-CAPS addresses
 * - Returns a single cleaned string (multi-address "&" joins preserved)
 */

/**
 * Convert a string to Title Case (handles ALL-CAPS input gracefully).
 * Words like "CA", "TX", "DR", "AV", "CT", "ST", "LN", "PL", "AVE", "BLVD" stay uppercase.
 */
const KEEP_UPPER = new Set([
    'CA', 'TX', 'NY', 'FL', 'WA', 'OR', 'NV', 'AZ', 'CO', 'UT', 'NJ', 'CT',
    'DR', 'AV', 'AVE', 'BLVD', 'ST', 'LN', 'PL', 'RD', 'CIR', 'WAY', 'TER', 'HWY', 'SQ',
    'NE', 'NW', 'SE', 'SW', 'N', 'S', 'E', 'W',
]);

const toTitleCase = (str) => {
    if (!str) return '';
    return str.split(' ').map(word => {
        const upper = word.toUpperCase();
        if (KEEP_UPPER.has(upper)) return upper;
        // If it's a zip code, keep as-is
        if (/^\d+$/.test(word)) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
};

/**
 * Clean a single address segment:
 * 1. Remove ", USA" / ", United States"
 * 2. Remove ZIP+4 suffix (e.g. 95070-3335 → 95070)
 * 3. Title-case if all uppercase
 */
const cleanSingle = (addr) => {
    if (!addr) return '';
    let cleaned = addr.trim();

    // Remove country suffixes
    cleaned = cleaned.replace(/,?\s*USA$/i, '').replace(/,?\s*United States$/i, '').trim();

    // Remove ZIP+4 extensions: match 5-digit zip followed by dash and 4 digits
    cleaned = cleaned.replace(/(\b\d{5})-\d{4}\b/g, '$1');

    // Title-case if the address looks ALL-CAPS (more than 60% uppercase letters)
    const letters = cleaned.replace(/[^a-zA-Z]/g, '');
    if (letters.length > 0) {
        const upperRatio = letters.replace(/[^A-Z]/g, '').length / letters.length;
        if (upperRatio > 0.6) {
            cleaned = toTitleCase(cleaned);
        }
    }

    return cleaned.trim();
};

/**
 * Main exported function: cleans an address string.
 * If the address contains "&" (merged properties), each segment is cleaned individually.
 * Returns a single cleaned string with " & " as separator.
 */
export const cleanAddress = (address) => {
    if (!address) return '';
    
    // Split on " & " for merged addresses
    const parts = address.split(/\s*&\s*/);
    return parts.map(cleanSingle).filter(Boolean).join(' & ');
};

/**
 * Returns an array of cleaned address parts for rendering as separate items.
 * Useful for multi-address merged properties where you want to render
 * each address on its own line or as a pill/badge.
 */
export const getAddressParts = (address) => {
    if (!address) return [];
    const parts = address.split(/\s*&\s*/);
    return parts.map(cleanSingle).filter(Boolean);
};
